import { createSelector } from 'reselect';

import {
  unitAdvancementPermissions,
  unitRosterYouthDetailsPermissions,
} from '@modules/roster-lite/constants';
import { userDataInitializedSel } from '@modules/user/duck/selectors';
import {
  isParentOrYouthMemberSel,
  isYouthMemberSel,
  newPermissionsSel,
} from '@shared/duck/selectors/general.selectors';
import { newPermissions } from '@shared/newPermissions';

/**
 * Returns `true` when the current user's effective permissions for the selected
 * organization include `VIEW_ROSTER_LITE` **and** do NOT include `VIEW_ROSTER_PAGE`.
 *
 * `VIEW_ROSTER_LITE` is granted by limited-permission positions (Committee
 * Member, Club Sponsor, Unit/Venturing College Scouter Reserve, Unit Scouter
 * Reserve, Post Committee Member, Executive Officer). The explicit
 * `!VIEW_ROSTER_PAGE` guard ensures a user who ALSO holds a full-roster
 * position in the same org (e.g., Committee Member + Cubmaster) is redirected to
 * the standard `/roster` view, not lite.
 *
 * Used by the sidebar nav to show the lite roster link instead of `/roster`.
 */
export const hasRosterLiteAccessSel = createSelector(
  newPermissionsSel,
  permissions =>
    Boolean(
      permissions[newPermissions.VIEW_ROSTER_LITE] &&
        !permissions[newPermissions.VIEW_ROSTER_PAGE],
    ),
);

/**
 * Access check for the youth/parent unit-roster pages (`/unit-roster/*`):
 * `ready`: user data has finished initializing (avoids a false 403 flash on a cold deep-link)
 *
 * `allowed`: the user is a youth member or a parent/guardian in the selected org
 */
export const canViewUnitRosterSel = createSelector(
  userDataInitializedSel,
  isParentOrYouthMemberSel,
  (ready, allowed) => ({ ready, allowed }),
);

/**
 * `true` when the CURRENT USER may open a youth's advancement details from the
 * unit roster, by either of two routes:
 *
 * - any Scout-First Class edit/approve permission, from the "Requirement
 *   Editor/Approver" positions assignable to youth. Open to any viewer;
 * - a unit-wide advancement role, but **only for an adult**. A parent who is
 *   also the unit's Advancement Chair reaches these pages this way.
 *
 * Every "Youth Member" role carries `EDIT_ADVANCEMENTS`, so admitting it
 * unconditionally would open the pages to every youth in the unit
 */
export const canViewYouthAdvancementDetailsSel = createSelector(
  newPermissionsSel,
  isYouthMemberSel,
  (permissions, isYouth) => {
    const has = (list: string[]) => list.some(p => Boolean(permissions[p]));

    return (
      has(unitRosterYouthDetailsPermissions) ||
      (!isYouth && has(unitAdvancementPermissions))
    );
  },
);
